<template>
  <v-card>
    <v-card-title class="py-2">
      <v-icon class="mr-2">merge_type</v-icon>
      <div class="title info--text">Aliases</div>
      <div class="title pl-1"> ⋅ </div>
      <div class="caption pl-1"> {{ aliases.length }} {{ pluralize('alias', aliases.length, 'es') }}</div>
    </v-card-title>
    <v-divider />
    <template v-if="editForm">
      <v-card-actions>
        <v-btn @click="editName()" color="success" small>
          <v-icon class="mr-1">add</v-icon>
          Add..
        </v-btn>
        <v-btn @click="addAlias()" color="warning" small>
          <v-icon class="mr-1">merge_type</v-icon>
          Merge existing..
        </v-btn>
      </v-card-actions>
      <v-divider />
    </template>

    <v-card-text>
      <p>
        <strong>Aliases</strong> for this data source store a subset of data. To preserve that
        data, these aliases cannot be removed but can be separated from the current data source.
      </p>
      <p>
        <strong>Name aliases</strong> are alternative names for this data source.
      </p>
    </v-card-text>
    <v-data-table hide-actions :items="nameAliases" no-data-text="No name aliases" hide-headers>
      <template v-slot:items="{ item }">
        <td class="text-xs-left">
          <v-chip label small>
            {{ item.Name }}
          </v-chip>
        </td>
        <td class="text-xs-right">
          <span v-if="item.IsPrimary" class="caption" :class="[matchesLocationName(item) ? 'info--text' : 'warning--text']">
            Primary
            <Tooltip :text="makeTooltipText(item)" small />
          </span>
          <div v-show="editForm && !item.IsPrimary">
            <v-btn v-if="isSystemAdmin" @click="makePrimary(item)" :loading="item.makePrimaryLoading" color="info" small>
              <v-icon class="mr-2">admin_panel_settings</v-icon>
              <span class="text--capitalize">
                Make primary
              </span>
            </v-btn>
            <v-tooltip :open-delay="500" left>
              <template v-slot:activator="{ on }">
                <v-btn v-show="editForm && !item.IsPrimary" v-on="on" @click="removeAlias(item)" icon>
                  <v-icon color="warning">call_split</v-icon>
                </v-btn>
              </template>
              <span>Separate alias from current data source</span>
            </v-tooltip>
          </div>
        </td>
      </template>
    </v-data-table>
    <v-divider />

    <v-card-text>
      <strong>QA/QC alias</strong> data are
      classified as QA/QC Replicate Samples.
    </v-card-text>
    <v-data-table hide-actions :items="qaqcAliases" no-data-text="No QA/QC aliases" hide-headers>
      <template v-slot:items="{ item }">
        <td class="text-xs-left">
          <v-chip label small>
            {{ item.Name }}
          </v-chip>
        </td>
        <td class="text-xs-right">
          <span v-if="item.IsPrimary" class="caption" :class="[matchesLocationName(item) ? 'info--text' : 'warning--text']">
            Primary
            <Tooltip :text="makeTooltipText(item)" small />
          </span>
          <div v-show="editForm && !item.IsPrimary">
            <v-btn v-if="isSystemAdmin" @click="makePrimary(item)" :loading="item.makePrimaryLoading" color="info" small>
              <v-icon class="mr-2">admin_panel_settings</v-icon>
              <span class="text--capitalize">
                Make primary
              </span>
            </v-btn>
            <v-tooltip :open-delay="500" left>
              <template v-slot:activator="{ on }">
                <v-btn v-show="editForm && !item.IsPrimary" v-on="on" @click="removeAlias(item)" icon>
                  <v-icon color="warning">call_split</v-icon>
                </v-btn>
              </template>
              <span>Separate alias from current data source</span>
            </v-tooltip>
          </div>
        </td>
      </template>
    </v-data-table>
    <v-divider />

    <v-card-text>
      <strong>Depth/Height alias</strong> data are assigned the specified depth/height values.
    </v-card-text>
    <v-data-table hide-actions :items="depthHeightAliases" no-data-text="No Depth/Height aliases" hide-headers>
      <template v-slot:items="{ item }">
        <td class="text-xs-left">
          <v-chip label small>
            {{ item.Name }}
          </v-chip>
        </td>
        <td class="text-xs-left">
          <div v-html="formatDepthHeightValues(item.DepthHeightStartValue, item.DepthHeightEndValue)" />
          <div class="caption">
            {{ item.Datum && item.Datum.DatumName }}
            ({{ item.Datum && item.Datum.DatumType.Name }})
          </div>
        </td>
        <td class="text-xs-right">
          <span v-if="item.IsPrimary" class="caption" :class="[matchesLocationName(item) ? 'info--text' : 'warning--text']">
            Primary
            <Tooltip :text="makeTooltipText(item)" small />
          </span>
          <div v-show="editForm && !item.IsPrimary">
            <v-btn v-if="isSystemAdmin" @click="makePrimary(item)" :loading="item.makePrimaryLoading" color="info" small>
              <v-icon class="mr-2">admin_panel_settings</v-icon>
              <span class="text--capitalize">
                Make primary
              </span>
            </v-btn>
            <v-tooltip :open-delay="500" left>
              <template v-slot:activator="{ on }">
                <v-btn v-show="editForm && !item.IsPrimary" v-on="on" @click="removeAlias(item)" icon>
                  <v-icon color="warning">call_split</v-icon>
                </v-btn>
              </template>
              <span>Separate alias from current data source</span>
            </v-tooltip>
          </div>
        </td>
      </template>
    </v-data-table>
    <AddAliasDialog ref="addAliasDialog" />
    <UnmergeDialog ref="unmergeDialog" />
    <MergeDialog ref="mergeDialog" :monitoringFeature="currentMonitoringFeature" />
  </v-card>
</template>

<script>
  import AddAliasDialog from './EditName/AddAliasDialog'
  import MergeDialog from './MergeDialog'
  import UnmergeDialog from './UnmergeDialog'

  import {
    SOURCE_TYPES,
  } from '@/store/MonitoringFeature/monitoringfeature'

  import {
    createNamespacedHelpers,
  } from 'vuex'

  import {
    formatDepthHeightValues,
  } from '@/helpers/depthHeight'

  const monitoringFeatures = createNamespacedHelpers('monitoringfeature')
  const dataSources = createNamespacedHelpers('datasource')
  const synonyms = createNamespacedHelpers('synonyms')
  const authenticate = createNamespacedHelpers('authenticate')

  import {
    ALIAS_TYPES,
  } from '@/helpers/synonyms'

  import {
    pluralize,
  } from '@/helpers/strings'

  export default {
    components: {
      AddAliasDialog,
      MergeDialog,
      UnmergeDialog,
    },
    props: {
      editForm: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      ALIAS_TYPES,
      headers: [{
        text: 'Alias Name',
        value: 'Name',
        sortable: false,
      },
      {
        text: 'Alias Type',
        value: 'type',
        sortable: false,
      },
      {
        text: 'Details',
        value: 'details',
        sortable: false,
      },
      {
        text: '',
        value: 'action',
        sortable: false,
      },
      ],
    }),
    computed: {
      ...monitoringFeatures.mapGetters(['currentFeatureDataSourceIndexByType', 'currentMonitoringFeature']),
      ...dataSources.mapGetters(['dataSourceIndex']),
      ...authenticate.mapGetters(['isSystemAdmin']),
      discreteDataSource() {
        return this.currentFeatureDataSourceIndexByType[SOURCE_TYPES.discreteEnvirodata]
      },
      discreteStation() {
        if (!this.discreteDataSource) return []
        return this.dataSourceIndex[SOURCE_TYPES.discreteEnvirodata][this.discreteDataSource.SourceId]
      },
      dataSourceAliases() {
        return this.discreteStation && this.discreteStation.Synonyms || []
      },
      nameAliases() {
        return this.dataSourceAliases.filter(x => !x.IsQaQc && !x.IsDepthHeightDatum)
      },
      qaqcAliases() {
        return this.dataSourceAliases.filter(x => x.IsQaQc)
      },
      depthHeightAliases() {
        return this.dataSourceAliases.filter(x => x.IsDepthHeightDatum)
      },
      aliases() {
        let aliases = this.nameAliases.map(alias => ({
          ...alias,
          type: ALIAS_TYPES.alias,
        }))

        let qaqcAliases = this.qaqcAliases.map(alias => ({
          ...alias,
          type: ALIAS_TYPES.qaqcAlias,
        }))

        let depthHeightAliases = this.depthHeightAliases.map(alias => ({
          ...alias,
          type: ALIAS_TYPES.depthHeightAlias,
        }))

        let result = [...aliases, ...qaqcAliases, ...depthHeightAliases]
        return result
      },
    },
    methods: {
      ...synonyms.mapActions(['updateSynonym']),
      ...dataSources.mapActions(['fetchDataSources']),
      formatDepthHeightValues,
      pluralize,
      makeTooltipText(primaryAlias) {
        let hasMatchingPrimaryAlias = primaryAlias.Name == this.currentMonitoringFeature.DisplayName
        let nonPrimaryText =
          '<br/><div><strong>Note:</strong> The primary alias is currently different than the location name.</div>'
        let result = '<div>The primary name shows up in reports for this data source.</div>'
        if (!hasMatchingPrimaryAlias) result += nonPrimaryText

        return result
      },
      editName() {
        this.$refs.addAliasDialog.show()
      },
      addAlias() {
        this.$refs.mergeDialog.show()
      },
      removeAlias(alias) {
        this.$refs.unmergeDialog.show(alias)
      },
      matchesLocationName(alias) {
        return alias.Name == this.currentMonitoringFeature.DisplayName
      },
      async makePrimary(alias) {
        this.$set(alias, 'makePrimaryLoading', true)

        try {
          let payload = {
            synonymId: alias.Id,
            isPrimary: true,
            synonymName: alias.Name,
            sourceType: SOURCE_TYPES.discreteEnvirodata,
          }

          await this.updateSynonym(payload)
          await this.fetchDataSources({
            sourceTypes: [SOURCE_TYPES.discreteEnvirodata],
          })
        } catch (error) {
          console.error(error)
        }

        this.$set(alias, 'makePrimaryLoading', false)
      },
    },
  }
</script>